/**
 * Human labels for the opaque ids the admin surfaces render — a user's email
 * and a track's name — resolved from the admin `/users` and `/tracks` lists the
 * SPA already reads (see `lib/data-provider.ts`).
 *
 * Why the lookup lives in the client and not on the approvals wire: the
 * `tf_admin` role holds `SELECT` on `tf_approval` / `tf_approval_decision` and
 * nothing else (drizzle/0014_approvals.sql), so an `adminList` join onto
 * `tf_user` / `tf_track` would need fresh grants and RLS policies for a purely
 * cosmetic column. COP-134's stance on widening approval rows stays intact and
 * no new field reaches any wire.
 *
 * Pure on purpose — no `@/lib/hub-client` import, which constructs a HubClient
 * at module load and would drag a real client into the unit test. The fetching
 * half is `use-principal-labels.ts`.
 */
import type { TfTrackRow, TfUserRow } from '@verybigthings/trackframe-shared/schemas';

/**
 * Method syntax (not properties with arrow types) per the repo convention:
 * under `strictFunctionTypes` an arrow-typed property compares its parameters
 * contravariantly, which rejects narrower real implementations.
 */
export interface PrincipalLabels {
  /** The user's email, or `undefined` when the id is unknown to the lookup. */
  userEmail(userId: string): string | undefined;
  /** The track's display name (falling back to its slug), or `undefined`. */
  trackName(trackId: string): string | undefined;
}

/**
 * Builds the two id → label maps. An unknown id resolves to `undefined` rather
 * than to a placeholder string, so callers keep rendering the raw id and an
 * incomplete lookup never masquerades as a resolved name.
 */
export function buildPrincipalLabels(
  users: readonly TfUserRow[],
  tracks: readonly TfTrackRow[],
): PrincipalLabels {
  const emails = new Map(users.map((u) => [u.id, u.email]));
  // `displayName` is the operator-facing label; `name` is the registration
  // slug and is always present, so the fallback never yields an empty cell.
  const names = new Map(tracks.map((t) => [t.id, t.displayName ?? t.name]));
  return {
    userEmail(userId) {
      return emails.get(userId);
    },
    trackName(trackId) {
      return names.get(trackId);
    },
  };
}

/** The empty lookup — every id resolves to `undefined`. */
export const EMPTY_PRINCIPAL_LABELS: PrincipalLabels = buildPrincipalLabels([], []);
